import { Draft } from 'src/types/common';
import { IAIRequest } from 'src/types/org/ai/schoolInterface';
import { ISummary } from 'src/types';

/**
 * Transforms raw data from the API into a properly structured IAIRequest object
 * @param rawData - Raw data from the API
 * @param item - Summary item containing metadata
 * @returns Transformed IAIRequest object
 */
export const transformToIAIRequest = (rawData: any, item: ISummary): Draft<IAIRequest> => {
  return {
    categoryId: rawData.categoryId || item.categoryId,
    userId: rawData.userId || item.userId,
    appliedLevel: rawData.appliedLevel || rawData?.data?.ai.appliedLevel || '1',
    data: {
      version:  'v3',
      ai: {
        appliedLevel: rawData.appliedLevel || rawData?.data?.ai.appliedLevel || '1'
      },
      org: {
        name:  rawData?.data?.org?.name || rawData?.schoolName || '',
        email: rawData?.data?.org?.email || '',
        phone:  rawData?.data?.org?.phone || '',
        website: rawData?.data?.org?.website || '',
        visitingAddress: rawData?.data?.org?.visitingAddress || {},
        shippingAddress: rawData?.data?.org?.shippingAddress,
        shippingPolicyException: rawData?.data?.org?.shippingPolicyException || undefined
      },
      appForm: {
        isSchoolDistrictMember: rawData.data?.appForm?.isSchoolDistrictMember,
        schoolDistricts:  rawData.data?.appForm?.schoolDistricts || '',
        establishedDate:  rawData.data?.appForm?.establishedDate || '',
        sharedOrgAddress: rawData.data?.appForm?.sharedOrgAddress,
        sharedOrgAddressInfo: rawData.data?.appForm?.sharedOrgAddressInfo,
        existingOrg: rawData.data?.appForm?.existingOrg,
        existingOrgInfo: rawData.data?.appForm?.existingOrgInfo,
        hasCollegeBoardCode: rawData.data?.appForm?.hasCollegeBoardCode,
        collegeBoardCodes: rawData.data?.appForm?.collegeBoardCodes || '',
        mergerSchoolCodes: rawData.data?.appForm?.mergerSchoolCodes || '',
        type: rawData.data?.appForm?.type || [],
        typeOther: rawData.data?.appForm?.typeOther || '',
        creditType: rawData.data?.appForm?.creditType || [],
        creditTypeOther: rawData.data?.appForm?.creditTypeOther || '',
        academicDisciplines: rawData.data?.appForm?.academicDisciplines || [],
        academicDisciplinesOther: rawData.data?.appForm?.academicDisciplinesOther || '',
        teachingMethod: rawData.data?.appForm?.teachingMethod || [],
        teachingMethodOther: rawData.data?.appForm?.teachingMethodOther || '',
        onsiteStudents: rawData.data?.appForm?.onsiteStudents || {
          zeroTaught: '',
          oneTaught: '',
          twoTaught: '',
          threeTaught: '',
          fourTaught: '',
          fiveTaught: '',
          sixTaught: '',
          sevenTaught: '',
          eightTaught: '',
          nineTaught: '',
          tenTaught: '',
          elevenTaught: '',
          twelveTaught: ''
        },
        studentsEnrolled: rawData.data?.appForm?.studentsEnrolled || {
          zeroEnrolled: '',
          oneEnrolled: '',
          twoEnrolled: '',
          threeEnrolled: '',
          fourEnrolled: '',
          fiveEnrolled: '',
          sixEnrolled: '',
          sevenEnrolled: '',
          eightEnrolled: '',
          nineEnrolled: '',
          tenEnrolled: '',
          elevenEnrolled: '',
          twelveEnrolled: ''
        },
        examsInformation:  rawData.data?.appForm?.examsInformation || {
          interestedInAPCourses: undefined,
          apCoursesOffered: undefined,
          preAPCoursesOffered: undefined,
          confirmAPGradeLevels: undefined,
          interestedInPreAPCourses: undefined,
          interestedInPSATSuite: undefined,
          interestedInSAT: undefined,
          satAdministrationTypes: undefined,
          earliestAdministrationDate: undefined,
        },
        accreditation: rawData.data?.appForm?.accreditation || {
          hasAccreditation: undefined,
          accreditationDetails: undefined,
          materials: []
        },
        principalName:  rawData.data?.appForm?.principalName || '',
        principalEmail: rawData.data?.appForm?.principalEmail || '',
        principalWebpage: rawData.data?.appForm?.principalWebpage || '',

        // On-Site Instruction Details fields
        studentSchedule: rawData.data?.appForm?.studentSchedule || {
          instructionDays: undefined,
          daySchedule: {
            sunday: { startTime: undefined, endTime: undefined },
            monday: { startTime: undefined, endTime: undefined },
            tuesday: { startTime: undefined, endTime: undefined },
            wednesday: { startTime: undefined, endTime: undefined },
            thursday: { startTime: undefined, endTime: undefined },
            friday: { startTime: undefined, endTime: undefined },
            saturday: { startTime: undefined, endTime: undefined}
          },
          hoursOnsite: {
            sunday: undefined,
            monday: undefined,
            tuesday: undefined,
            wednesday: undefined,
            thursday: undefined,
            friday: undefined,
            saturday: undefined
          }
        },

        // AI Test Prep fields
        aiTestPrep: rawData.data?.appForm?.aiTestPrep || {
          hasTestPrep: undefined,
          description: undefined,
          schedule: undefined,
          onlyForEnrolledStudents: undefined,
          hasStudentFees: undefined,
          isOutsideCompany: undefined,
          hasInstructorCompensation: undefined,
          hasAccessToSecureMaterials: undefined
        },

        // AI Staff fields
        aiStaff:  rawData.data?.appForm?.aiStaff || {
          totalStaff: undefined,
          hasRelativeEmployees: undefined,
          relativeEmployeeCount: undefined
        },
        // Exam Security fields
        examSecurity: rawData.data?.appForm?.examSecurity || {
          heldAtVisitingAddress: undefined,
          alternativeTestingAddress: undefined, // NEW: Address object
          alternativeAddressExplanation: undefined,
          materialReceivedBy: undefined,
          materialReceivingArea: undefined,
          materialReceivingAreaOther: undefined,
          materialStorageLocation: undefined,
          isMaterialStorageLocked: undefined,
          securityResponsible: undefined,
          allowNonEmployedAccess: undefined,
          hasDesignatedTestingRooms: undefined,
          hasTestingRoomPowerWifi: undefined
        },
        submitterComments: rawData.data?.appForm?.submitterComments || ''
      },
      testingPlan: rawData?.testingPlan || rawData.data?.testingPlan || {},
    },
    meta: {
      creator: rawData.creator || item.creator,
      creatorEmail: rawData.creatorEmail || item.creatorEmail,
      submitter: rawData?.meta?.submitter || '',
      disclaimer: rawData?.meta?.disclaimer || false
    }
  };
};

/**
 * Calculates total on-site hours for selected instruction days
 * @param instructionDays - Array of selected days (e.g., ['monday', 'tuesday'])
 * @param hoursOnsite - Object with hours for each day (e.g., { monday: '7', tuesday: '8' })
 * @returns true if total hours < 15, false otherwise
 */
export const isOnsiteHoursLessThan15 = (
  instructionDays?: string[],
  hoursOnsite?: Record<string, string>
): boolean => {
  // Return false if no instruction days selected
  if (!instructionDays || instructionDays.length === 0) {
    return false;
  }

  // Return false if no hours data
  if (!hoursOnsite) {
    return false;
  }

  let totalHours = 0;

  // Sum hours only for selected instruction days
  instructionDays.forEach(day => {
    const dayKey = day.toLowerCase();
    const hours = hoursOnsite[dayKey];

    if (hours && !isNaN(Number(hours))) {
      totalHours += Number(hours);
    }
  });
  console.log(' isOnsiteHoursLessThan15() : Total Onsite Hours:', totalHours);
  return totalHours < 15;
};